import {RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {APP_INITIALIZER, ModuleWithProviders, NgModule} from '@angular/core';
import {FlexLayoutModule} from '@angular/flex-layout';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {PinchZoomModule} from 'ngx-pinch-zoom';

import {BillOfMaterialsModule} from './bill-of-materials.module';
import {SharedConfig} from './shared.config';
import {LoginService} from './login.service';
import {MsgBoxService} from './msg-box.service';
import {ResizeService} from './resize.service';
import {UsersService} from './users.service';
import {PermissionsService} from './permissions.service';
import {ConfigurationService} from './configuration.service';
import {HierarchyService} from './hierarchy.service';
import {CustomerCareService} from './customer-care.service';
import {CapabilitiesService} from './capabilities.service';
import {NetworkService} from './network.service';
import {NotificationsService} from './notifications.service';
import {MenuService} from './menu.service';
import {DialogService} from './dialog/dialog.service';
import {MomentDateModule} from '@angular/material-moment-adapter';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AgmCoreModule} from '@agm/core';
import {CommonsModule} from '../commons/commons.module';
import {I18nModule} from '../i18n/i18n.module';
import {MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {DATE_FORMATS} from '../i18n/i18n-defaults';
import {fas} from '@fortawesome/pro-solid-svg-icons';
import {fal} from '@fortawesome/pro-light-svg-icons';
import {DialogComponent} from './dialog/dialog.component';
import {DividerComponent} from './divider/divider.component';
import {IconComponent} from './icon/icon.component';
import {LineChartComponent} from './line-chart/line-chart.component';
import {TimeFilterComponent} from './time-filter/time-filter.component';
import {SeveritySelectorComponent} from './severity-selector/severity-selector.component';
import {PeriodicRefreshComponent} from './periodic-refresh/periodic-refresh.component';
import {EntityPathComponent} from './entity-path/entity-path.component';
import {PlanningDetailsComponent} from './planning-details/planning-details.component';
import {ForecastDetailsComponent} from './forecast-details/forecast-details.component';
import {SharedEnergyChartComponent} from './shared-energy-chart/shared-energy-chart.component';
import {GoogleMapsComponent} from './google-maps/google-maps.component';
import {TooltipFormatterPipe, YTickFormatterPipe} from './line-chart/line-chart.pipe';
import {SharedEnergyTooltipFormatterPipe} from './shared-energy-chart/shared-energy-chart.pipe';
import {
    ECDocumentTypeI18nPipe,
    ECDocumentTypePipe,
    ECMemberStateIconPipe,
    ECMemberStatePipe,
    ECMemberJuridicalTypePipe,
    ECMemberLegalFormTypePipe,
    ECMemberSubjectTypePipe,
    ECMemberEntitySubtypePipe,
    ECRepresentativeTypePipe,
    ECModePipe,
    ECSubjectAccessTypeIconPipe,
    ECSubjectParticipationTypeIconPipe,
    EntityIconPipe,
    EntityNicePathPipe,
    EntityTypePipe,
    ForecastStrategyPipe,
    GenericPipe,
    MeterModelPipe,
    NiceValuePipe,
    NotificationSeverityIconPipe,
    NotificationSeverityPipe,
    NotificationStatusPipe,
    PerformanceIconPipe,
    PlanningSourcePipe,
    PlanningStatusIconPipe,
    PlanningStatusPipe,
    PlanningStrategyPipe,
    PredictiveModelIconPipe,
    PredictiveModelTypePipe,
    QuantityNamePipe,
    RadioRateModePipe,
    RadioCacvModePipe,
    CacvModePipe,
    RateModePipe,
    StrategyPipe,
    RedistributedAlgorithmPipe,
    UserTypePipe,
    DataSourcePipe,
    ECMonthStatusPipe,
    FilteringTypesTranslationPipe
} from './shared.pipe';
import {ECResourceTreeComponent} from './ec-resource-tree/ec-resource-tree.component';
import {ECMeasureAggregationsComponent} from './ec-measure-aggregations/ec-measure-aggregations.component';
import {PlanningsComponent} from './plannings/plannings.component';
import {ForecastsComponent} from './forecasts/forecasts.component';
import {WizardPodCardComponent} from './wizard-pod-card/wizard-pod-card.component';
import {StatisticalProfileComponent} from './statistical-profile/statistical-profile.component';
import {PodProfileComponent} from './statistical-profile/pod-profile/pod-profile.component';
import {PredictiveModelComponent} from './predictive-model/predictive-model.component';
import {
    GoogleMapsAddressAutocompleteComponent
} from './google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {AutocompleteDropdownComponent} from './autocomplete-dropdown/autocomplete-dropdown.component';
import {I18NService} from '../i18n/i18n.service';

function dateAdapterFactory(i18NService: I18NService): string {
    const lang = i18NService.getLanguage();
    return lang ? lang : 'en'; // default lang
}

@NgModule({
    declarations: [
        DialogComponent,
        DividerComponent,
        IconComponent,
        LineChartComponent,
        TimeFilterComponent,
        SeveritySelectorComponent,
        PeriodicRefreshComponent,
        EntityPathComponent,
        PlanningDetailsComponent,
        ForecastDetailsComponent,
        SharedEnergyChartComponent,
        GoogleMapsComponent,
        GoogleMapsAddressAutocompleteComponent,
        AutocompleteDropdownComponent,
        PlanningsComponent,
        ForecastsComponent,
        ECResourceTreeComponent,
        ECMeasureAggregationsComponent,
        WizardPodCardComponent,
        StatisticalProfileComponent,
        PodProfileComponent,
        PredictiveModelComponent,
        YTickFormatterPipe,
        TooltipFormatterPipe,
        SharedEnergyTooltipFormatterPipe,
        EntityNicePathPipe,
        NiceValuePipe,
        EntityTypePipe,
        GenericPipe,
        QuantityNamePipe,
        EntityIconPipe,
        NotificationStatusPipe,
        NotificationSeverityPipe,
        NotificationSeverityIconPipe,
        PlanningStatusPipe,
        PlanningStatusIconPipe,
        PlanningSourcePipe,
        PlanningStrategyPipe,
        ForecastStrategyPipe,
        ECMemberJuridicalTypePipe,
        ECMemberLegalFormTypePipe,
        ECMemberSubjectTypePipe,
        ECMemberEntitySubtypePipe,
        ECRepresentativeTypePipe,
        ECMemberStatePipe,
        ECMemberStateIconPipe,
        ECSubjectParticipationTypeIconPipe,
        ECSubjectAccessTypeIconPipe,
        ECDocumentTypePipe,
        ECDocumentTypeI18nPipe,
        PredictiveModelTypePipe,
        UserTypePipe,
        RateModePipe,
        RadioRateModePipe,
        RadioCacvModePipe,
        CacvModePipe,
        PredictiveModelIconPipe,
        StrategyPipe,
        RedistributedAlgorithmPipe,
        PerformanceIconPipe,
        ECModePipe,
        MeterModelPipe,
        GoogleMapsAddressAutocompleteComponent,
        ECMonthStatusPipe,
        DataSourcePipe,
        FilteringTypesTranslationPipe,
    ],
    imports: [
        CommonModule,
        RouterModule,
        BillOfMaterialsModule,
        FlexLayoutModule,
        FontAwesomeModule,
        MomentDateModule,
        FormsModule,
        ReactiveFormsModule,
        PinchZoomModule,
        AgmCoreModule,
        // application
        CommonsModule,
        I18nModule
    ],
    exports: [
        // modules
        BillOfMaterialsModule,
        FlexLayoutModule,
        FontAwesomeModule,
        PinchZoomModule,
        // components
        DialogComponent,
        DividerComponent,
        IconComponent,
        LineChartComponent,
        TimeFilterComponent,
        SeveritySelectorComponent,
        PeriodicRefreshComponent,
        EntityPathComponent,
        PlanningDetailsComponent,
        ForecastDetailsComponent,
        SharedEnergyChartComponent,
        GoogleMapsComponent,
        PlanningsComponent,
        ForecastsComponent,
        ECResourceTreeComponent,
        ECMeasureAggregationsComponent,
        WizardPodCardComponent,
        StatisticalProfileComponent,
        PodProfileComponent,
        PredictiveModelComponent,
        GoogleMapsAddressAutocompleteComponent,
        AutocompleteDropdownComponent,
        // pipes
        YTickFormatterPipe,
        TooltipFormatterPipe,
        SharedEnergyTooltipFormatterPipe,
        EntityNicePathPipe,
        NiceValuePipe,
        EntityTypePipe,
        GenericPipe,
        QuantityNamePipe,
        EntityIconPipe,
        NotificationStatusPipe,
        NotificationSeverityPipe,
        NotificationSeverityIconPipe,
        PlanningStatusPipe,
        PlanningStatusIconPipe,
        PlanningSourcePipe,
        PlanningStrategyPipe,
        ForecastStrategyPipe,
        ECMemberJuridicalTypePipe,
        ECMemberLegalFormTypePipe,
        ECMemberSubjectTypePipe,
        ECMemberEntitySubtypePipe,
        ECRepresentativeTypePipe,
        ECMemberStatePipe,
        ECMemberStateIconPipe,
        ECSubjectParticipationTypeIconPipe,
        ECSubjectAccessTypeIconPipe,
        ECDocumentTypePipe,
        ECDocumentTypeI18nPipe,
        PredictiveModelTypePipe,
        UserTypePipe,
        RateModePipe,
        RadioRateModePipe,
        RadioCacvModePipe,
        CacvModePipe,
        PredictiveModelIconPipe,
        StrategyPipe,
        RedistributedAlgorithmPipe,
        PerformanceIconPipe,
        ECModePipe,
        MeterModelPipe,
        ECMonthStatusPipe,
        DataSourcePipe,
        FilteringTypesTranslationPipe
    ]
})
export class SharedModule {

    constructor(library: FaIconLibrary) {
        library.addIconPacks(fal, fas);  // FAL and FAS are the pro icons
    }

    /**
     * Configures the CommonsModule.
     *
     * @param config Module configuration
     * @return The module with the providers
     */
    static forRoot(config: SharedConfig): ModuleWithProviders<SharedModule> {
        return {
            ngModule: SharedModule,
            providers: [
                // services
                LoginService,
                MsgBoxService,
                ResizeService,
                UsersService,
                PermissionsService,
                ConfigurationService,
                HierarchyService,
                CustomerCareService,
                CapabilitiesService,
                NetworkService,
                NotificationsService,
                MenuService,
                DialogService,
                {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS},
                {provide: SharedConfig, useValue: config},
                // enable use of (already added) setup hooks by LoginService
                // NOTE: `deps` must include *all* services which set up some hooks, starting from LoginService
                {
                    provide: APP_INITIALIZER,
                    useFactory: (loginService: LoginService) => () => loginService.enableSetupHooks(),
                    deps: [
                        LoginService,
                        HierarchyService, PermissionsService, UsersService, NetworkService, ConfigurationService, CapabilitiesService, CustomerCareService
                    ],
                    multi: true
                },
                {provide: MAT_DATE_LOCALE, useFactory: dateAdapterFactory, deps: [I18NService]}
            ]
        };
    }
}
